export const CLOCK_SKEW_WARNING_MS = 60_000;
const MAX_ROUND_TRIP_MS = 5_000;

export type DeviceClockStatus = 'unknown' | 'synced' | 'skewed';

/**
 * Compare an uncached, same-origin time sample against the whole request window.
 * Slow responses, cached responses and a clock change during the request are
 * inconclusive, not evidence of skew. This does not adjust any protocol clock.
 */
export async function checkDeviceClock(signal: AbortSignal): Promise<Exclude<DeviceClockStatus, 'unknown'>> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener('abort', abort, { once: true });
  if (signal.aborted) abort();
  const timeout = setTimeout(abort, 8_000);
  try {
    const requestId = crypto.randomUUID();
    const startedAt = Date.now();
    const startedMono = performance.now();
    const response = await fetch(`/api/time?requestId=${requestId}`, {
      signal: controller.signal,
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!response.ok) throw new Error('Clock check unavailable');
    const sample = await response.json();
    const receivedAt = Date.now();
    const elapsed = performance.now() - startedMono;
    if (
      controller.signal.aborted ||
      sample?.requestId !== requestId ||
      !Number.isSafeInteger(sample?.serverTime) ||
      sample.serverTime <= 0 ||
      elapsed < 0 ||
      elapsed > MAX_ROUND_TRIP_MS ||
      Math.abs(receivedAt - startedAt - elapsed) > 1_000
    ) {
      throw new Error('Clock check inconclusive');
    }
    // The server sampled its clock between these two local timestamps. Only
    // warn when the entire possible offset is outside the tolerance.
    return sample.serverTime - receivedAt > CLOCK_SKEW_WARNING_MS || sample.serverTime - startedAt < -CLOCK_SKEW_WARNING_MS
      ? 'skewed'
      : 'synced';
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener('abort', abort);
  }
}
